
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { CalendarDays, User } from "lucide-react";
import { BlogPost, getAllPublishedBlogPosts } from "@/services/blog";
import { pagination, blogCategories } from "@/constants";
import { PaginationControl } from "@/components/shared/PaginationControl";
import { encodeS3Url } from "@/components/encodeS3Url";
import { capitalizeWords } from "@/helper";
import SEO from "@/components/seo/seo";

const Blog = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [totalPages, setTotalPages] = useState(1);

  const fetchPosts = async (page = pagination.page) => {
    setIsLoading(true);
    try {
      const queryParams: any = {
        page,
        limit: pagination.limit,
      };

      if (filter !== "All") {
        queryParams.category = filter;
      }

      const { data, pagination: paginationData } = await getAllPublishedBlogPosts(queryParams);

      if (Array.isArray(data)) {
        setPosts(data);
        if (paginationData) {
          setTotalPages(paginationData.totalPages);
        }
      } else {
        console.error("Unexpected API response format:", data);
      }
    } catch (error) {
      console.error("Error fetching blog posts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [filter]);

  const handleFilterChange = (category: string) => {
    setFilter(category); // This triggers useEffect
  };

  // Combine "All" with imported categories
  const categories = [{ label: "All", value: "All" }, ...blogCategories];

  return (
    <>
      {/* SEO */}
      <SEO
        title="Dubai Real Estate Blogs | Urban Oasis UAE"
        description="Insights, guides, and trends on Dubai's dynamic real estate market to help you make informed property decisions."
        url="/blogs"
        image="/image/logo.png"
        keywords="Dubai real estate, Dubai property, Dubai real estate blog, Dubai property blog"
      />
      <div className="flex-grow pt-32 pb-16">
        <div className="container mx-auto px-4">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Dubai PropVerse Blog</h1>
            <p className="text-muted-foreground max-w-2xl">
              Insights, guides, and trends on Dubai's dynamic real estate market to
              help you make informed property decisions.
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div className="flex gap-3 flex-wrap">
              {categories.map((category) => (
                <Badge
                  key={category.value}
                  className="cursor-pointer"
                  variant={filter === category.value ? "default" : "outline"}
                  onClick={() => handleFilterChange(category.value)}
                >
                  {category.label}
                </Badge>
              ))}
            </div>
          </div>

          {isLoading ? (
            <>
              {/* Featured Post Skeleton */}
              <Card className="mb-12 overflow-hidden">
                <div className="grid grid-cols-1 md:grid-cols-2">
                  <Skeleton className="h-[500px] w-full" />
                  <div className="p-6 md:p-8 flex flex-col justify-between">
                    <div>
                      <Skeleton className="h-6 w-24 mb-3" />
                      <Skeleton className="h-8 w-3/4 mb-3" />
                      <div className="space-y-2 mb-6">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-2/3" />
                      </div>
                      <div className="flex items-center gap-4 mb-6">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-4 w-32" />
                      </div>
                    </div>
                    <Skeleton className="h-10 w-40" />
                  </div>
                </div>
              </Card>

              {/* Blog Grid Skeleton */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Card key={i} className="overflow-hidden flex flex-col h-full">
                    <Skeleton className="w-full h-48" />
                    <CardHeader>
                      <Skeleton className="h-6 w-3/4 mb-2" />
                    </CardHeader>
                    <CardContent className="flex-grow">
                      <div className="space-y-2 mb-4">
                        <Skeleton className="h-3 w-full" />
                        <Skeleton className="h-3 w-full" />
                        <Skeleton className="h-3 w-2/3" />
                      </div>
                      <div className="flex items-center gap-4">
                        <Skeleton className="h-3 w-24" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                    </CardContent>
                    <CardFooter>
                      <Skeleton className="h-9 w-full" />
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </>
          ) : posts.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              No blog posts found.
            </div>
          ) : (
            <>
              {/* Featured Post - Just taking the first one for now */}
              {posts.length > 0 && (
                <Card className="mb-12 overflow-hidden">
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    <div className="h-[500px]">
                      <img
                        src={posts[0].featuredImage ? encodeS3Url(posts[0].featuredImage) : "/placeholder.svg"}
                        alt={posts[0].title}
                        className="w-full h-full object-cover min-h-[500px]"
                      />
                    </div>
                    <div className="p-6 md:p-8 flex flex-col justify-between">
                      <div>
                        <Badge className="mb-3">Featured</Badge>
                        <CardTitle className="text-2xl md:text-3xl mb-3">
                          {capitalizeWords(posts[0].title)}
                        </CardTitle>
                        <CardContent className="text-muted-foreground p-0 mb-6 line-clamp-3">
                          {posts[0].excerpt}
                        </CardContent>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
                          <div className="flex items-center">
                            <User className="h-4 w-4 mr-1" />
                            {posts[0].author}
                          </div>
                          <div className="flex items-center">
                            <CalendarDays className="h-4 w-4 mr-1" />
                            {posts[0].publishDate ? new Date(posts[0].publishDate).toLocaleDateString() : 'N/A'}
                          </div>
                        </div>
                      </div>
                      <CardFooter className="p-0">
                        <Button asChild>
                          <Link to={`/blogs/${posts[0].slug || posts[0]._id}`}>Read Full Article</Link>
                        </Button>
                      </CardFooter>
                    </div>
                  </div>
                </Card>
              )}

              {/* Blog Posts Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {posts.slice(1).map((post) => (
                  <Card key={post._id} className="overflow-hidden flex flex-col h-full">
                    <div className="relative">
                      <img
                        src={post.featuredImage ? encodeS3Url(post.featuredImage) : "/placeholder.svg"}
                        alt={post.title}
                        className="w-full h-48 object-cover"
                      />
                      <Badge className="absolute top-3 left-3">{post.category}</Badge>
                    </div>
                    <CardHeader>
                      <CardTitle className="line-clamp-2">{capitalizeWords(post.title)}</CardTitle>
                    </CardHeader>
                    <CardContent className="text-muted-foreground flex-grow">
                      <p className="line-clamp-3 mb-4">{post.excerpt}</p>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center">
                          <User className="h-4 w-4 mr-1" />
                          {post.author}
                        </div>
                        <div className="flex items-center">
                          <CalendarDays className="h-4 w-4 mr-1" />
                          {post.publishDate ? new Date(post.publishDate).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                    </CardContent>
                    <CardFooter>
                      <Button variant="ghost" asChild className="w-full">
                        <Link to={`/blogs/${post.slug || post._id}`}>Read More</Link>
                      </Button>
                    </CardFooter>
                  </Card>
                ))}

              </div>

            </>
          )}

          {/* Pagination Control - Kept mounted to preserve state */}
          {(!isLoading && posts.length === 0) ? null : (
            <div className={isLoading ? "hidden" : "mt-8"}>
              <PaginationControl
                totalPages={totalPages}
                onPageChange={fetchPosts}
                key={filter}
              />
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default Blog;
